"use client";

import type { RouterOutputs } from "@api/trpc/routers/_app";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import Fuse from "fuse.js";
import {
  ClipboardCheck,
  Copy,
  Headphones,
  LayoutList,
  SquarePen,
  Upload,
} from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parseAsString, useQueryState } from "nuqs";
import posthog from "posthog-js";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { DeleteModal } from "@/components/modals/delete-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { SelectSeparator } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import useAuthModal from "@/features/auth/hooks/use-auth-modal";
import { useGetFiles } from "@/features/files/api/use-get-files";
import { CreatePracticeTestModal } from "@/features/practice-test/components/create-practice-test-modal";
import { PracticeTestHistory } from "@/features/practice-test/components/practice-test-history";
import { useGetQuestions } from "@/features/questions/api/use-get-questions";
import EmptyFlashCard from "@/features/questions/components/empty-flash-card";
import ErrorFlashCard from "@/features/questions/components/error-flash-card";
import OrderQuestionDropdown from "@/features/questions/components/order-question-dropdown";
import { useCloneStudyset } from "@/features/studysets/api/use-clone-studyset";
import { useDeleteStudyset } from "@/features/studysets/api/use-delete-studyset";
import { useGetStudySet } from "@/features/studysets/api/use-get-studyset";
import { useToggleSaveStudySet } from "@/features/studysets/api/use-toggle-save-studyset";
import StudySetDropDown from "@/features/studysets/components/studyset-dropdown";
import { StudySetProgressCard } from "@/features/studysets/components/studyset-progress-card";
import useShareStudySetModal from "@/features/studysets/hooks/use-share-studyset-modal";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { getSlug, scrollToQuestion } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { NoteCard } from "../../ai-notetaker/components/note-card";
import { QuestionsList } from "./question-list";
import { QuestionPreview } from "./question-preview";
import RatingDialog from "./rating-dialog";
import RatingStarButton from "./rating-star-button";
import { SignUpCard } from "./sign-up-card";
import { UpgradeCard } from "./upgrade-card";

interface StudySetPublicViewProps {
  isAuthenticated: boolean;
  userId?: string;
  id: string;
  isSubscribed?: boolean;
  noteData?: RouterOutputs["notes"]["getNoteByStudySetId"];
}

const StudySetPublicView = ({
  isAuthenticated,
  isSubscribed,
  userId,
  id,
  noteData,
}: StudySetPublicViewProps) => {
  const [query] = useQueryState("q", parseAsString);
  const [queryOrder] = useQueryState("orderBy", parseAsString);
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useI18n();
  const {
    data: questionsWithFileName,
    status: questionStatus,
    isFetching,
    refetch,
  } = useGetQuestions({ id });
  const { data: files } = useGetFiles(id, { enabled: !!userId });
  const { data: studySet } = useGetStudySet(id);

  const { onOpen: openShareDialog } = useShareStudySetModal();
  const { onOpen: onOpenPricing } = usePricingModal();

  const showCta = useMemo(() => {
    // Return early if no questions
    if (!questionsWithFileName?.questions?.length) return false;

    if (!files || files.length === 0)
      // Return early if no files data
      return false;

    // Return false if user is subscribed
    if (isSubscribed) return false;

    // Check if any file meets our conditions
    return files.some((file) => {
      // Skip if file doesn't meet basic criteria
      if (!file || userId !== file.userId) {
        return false;
      }
      // Check if this file meets our chunk conditions
      return (
        file.totalQuizChunks >= 1 &&
        file.totalQuizChunks < file.totalUntruncatedQuizChunks
      );
    });
  }, [files, isSubscribed, userId, questionsWithFileName?.questions?.length]);

  const scrollToId = searchParams.get("scrollTo");
  useEffect(() => {
    if (scrollToId && questionsWithFileName?.questions.length) {
      // Small delay to ensure content is loaded

      scrollToQuestion(scrollToId);
    }
  }, [scrollToId, questionsWithFileName]);

  const sortQuestions = (
    questions: RouterOutputs["questions"]["getWithFileNames"]["questions"],
    orderBy: string | null,
  ) => {
    if (!questions) return [];

    const questionsCopy = [...questions];

    switch (orderBy) {
      // ascending order
      case "createdAt":
        return questionsCopy.sort(
          (a, b) =>
            new Date(a.questions.createdAt).getTime() -
            new Date(b.questions.createdAt).getTime(),
        );

      case "progress":
        return questionsCopy.sort(
          (a, b) =>
            (a.consecutiveCorrectAnswers || 0) -
            (b.consecutiveCorrectAnswers || 0),
        );

      case "alphabetical":
        return questionsCopy.sort((a, b) =>
          a.questions.question.localeCompare(b.questions.question),
        );

      default:
        return questionsCopy;
    }
  };

  const fuse = useMemo(() => {
    if (!questionsWithFileName) return null;
    return new Fuse(questionsWithFileName.questions, {
      keys: ["questions.question", "questions.answer"],
      threshold: 0.4,
    });
  }, [questionsWithFileName]);

  const filteredQuestions = useMemo(() => {
    let questions = questionsWithFileName?.questions;

    // Apply search filter if query exists
    if (fuse && query) {
      questions = fuse.search(query).map((result) => result.item);
    }

    // Apply sorting
    return sortQuestions(questions || [], queryOrder);
  }, [fuse, query, queryOrder, questionsWithFileName]);

  const _pathname = usePathname();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const _cloneStudySetMutation = useCloneStudyset();
  const likeStudySetMutation = useToggleSaveStudySet({ id });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPracticeTestOpen, setIsPracticeTestOpen] = useState(false);
  const deleteMutation = useDeleteStudyset({ id: studySet.id });
  const unLikeMutation = useToggleSaveStudySet({ id: studySet.id });
  const { onOpen: onOpenAuth } = useAuthModal();

  useEffect(() => {
    if (window.location.hash.includes("#section") && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [window.location.hash]);

  const handleUnlike = () => {
    setIsDeleteOpen(false);
    unLikeMutation.mutate({ id: studySet.id, isLiked: true });
  };

  const likeStudySet = (url: string) => {
    if (!isAuthenticated) {
      onOpenAuth();
      return;
    }
    router.push(url);
    if (!studySet.isLiked && !studySet.isOwner) {
      likeStudySetMutation.mutate({
        id: studySet.id,
        isLiked: false,
      });
    }
  };

  const _hasSuccessFiles = files?.some((f) => f.status === "success");

  const studyMethods = [
    {
      title: "quizModes.flashcards",
      icon: Copy,
      url: `/studysets/${studySet.id}/quiz?mode=flashcards`,
      disabled: false,
      isNew: false,
    },
    {
      title: "quizModes.multipleChoice",
      icon: LayoutList,
      url: `/studysets/${studySet.id}/quiz?mode=multiple_choice`,
      disabled: false,
      isNew: false,
    },
    {
      title: "quizModes.writtenQuiz",
      icon: SquarePen,
      url: `/studysets/${studySet.id}/quiz?mode=written`,
      disabled: false,
      isNew: false,
    },
    {
      title: "quizModes.audioStudy",
      icon: Headphones,
      url: `/studysets/${studySet.id}/audio-study`,
      disabled: false,
      isNew: true,
    },
    {
      title: "quizModes.practiceTest",
      icon: ClipboardCheck,
      url: `/studysets/${studySet.id}/test`,
      disabled: false,
      isNew: true,
      opensPracticeTestModal: true,
    },
  ] as const;

  const visibleMethods = studyMethods.filter(
    (m) => !("opensPracticeTestModal" in m) || isAuthenticated,
  );

  const publicUrl = `/studysets/${getSlug(studySet.name, studySet.id)}`;

  return (
    <>
      <div className="flex flex-col container py-4 md:py-8">
        <main className="flex flex-col flex-grow gap-8">
          <div className="flex flex-col gap-1">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h2 className="text-2xl md:text-3xl font-medium leading-tight md:leading-9 break-words truncate">
                {studySet.name}
              </h2>
              <div className="flex items-center gap-2 self-start md:self-auto">
                <Button
                  size="sm"
                  variant={"outline"}
                  onClick={() =>
                    openShareDialog({
                      publicUrl,
                      isPublic: studySet.isPublic,
                      studySetId: studySet.id,
                      type: studySet.isOwner ? "personal" : "liked",
                      isAuthenticated,
                    })
                  }
                  className="flex items-center gap-2"
                >
                  <Upload className="size-4" />
                  {t("share")}
                </Button>
                {isAuthenticated && (
                  <StudySetDropDown
                    studySet={studySet}
                    setIsDeleteOpen={setIsDeleteOpen}
                    trigger={
                      <Button
                        variant={"outline"}
                        size={"icon"}
                        className="size-9"
                      >
                        <DotsHorizontalIcon className="size-4" />
                      </Button>
                    }
                  />
                )}
              </div>
            </div>
            <div>
              {isAuthenticated ? (
                <Suspense
                  fallback={
                    <RatingStarButton studySet={studySet} disabled={true} />
                  }
                >
                  <RatingDialog
                    id={studySet.id}
                    totalReviews={studySet.totalRatings}
                    initialRating={studySet.averageRating}
                    canEdit={isAuthenticated && !studySet.isOwner}
                  />
                </Suspense>
              ) : (
                <RatingStarButton studySet={studySet} disabled={true} />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 justify-center items-center h-full gap-4 w-full">
            {visibleMethods.map((method) => (
              <button
                type="button"
                key={method.title}
                className="relative flex flex-col items-center justify-center border gap-4 py-6 px-4 rounded-2xl bg-muted/40 hover:bg-muted/80 transition-all duration-300 w-full h-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-muted/40"
                onClick={() => {
                  if (method.disabled) return;
                  if ("opensPracticeTestModal" in method) {
                    if (!isAuthenticated) {
                      onOpenAuth();
                      return;
                    }
                    setIsPracticeTestOpen(true);
                    return;
                  }
                  likeStudySet(method.url);
                }}
                disabled={method.disabled}
              >
                {method.isNew && (
                  <span className="absolute top-2 right-2 text-[10px] font-semibold bg-brand-primary text-white px-1.5 py-0.5 rounded-full">
                    NEW
                  </span>
                )}
                <method.icon className="size-6 text-brand-primary" />
                <p className="text-foreground text-base font-medium">
                  {/* @ts-ignore */}
                  {t(method.title)}
                </p>
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-8">
            {questionStatus === "pending" ? (
              <Skeleton className="h-[300px] md:h-[400px] w-full" />
            ) : questionStatus === "error" ? (
              <ErrorFlashCard retry={() => refetch()} />
            ) : !questionsWithFileName.questions.length ? (
              <EmptyFlashCard
                isPolling={questionsWithFileName?.status?.shouldPoll}
              />
            ) : (
              <QuestionPreview
                questions={questionsWithFileName?.questions}
                status={questionStatus}
              />
            )}
            <SelectSeparator className="w-full" />
            <div className="flex items-center gap-3">
              <Avatar className="size-9">
                <AvatarImage
                  alt="Profile Image"
                  src={studySet.profileImg ?? undefined}
                />
                <AvatarFallback>{studySet.author[0]}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <p className="text-xs font-normal text-muted-foreground">
                  {t("createdBy")}
                </p>
                <p className="text-foreground font-medium text-base leading-5">
                  {studySet.author}
                </p>
              </div>
            </div>
          </div>
          {noteData && (
            <div className="flex flex-col gap-4">
              <h4 className="font-semibold text-base">Notes in this Set</h4>
              <NoteCard
                note={{
                  ...noteData,
                  author: studySet.author,
                  profileImg: studySet.profileImg,
                  isOwner: false,
                }}
                isOwner={false}
                author={studySet.author}
                profileImg={studySet.profileImg}
              />
            </div>
          )}
          {isAuthenticated && (
            <PracticeTestHistory
              source={{ kind: "studyset", studySetId: studySet.id }}
            />
          )}
          {isAuthenticated && !isSubscribed ? (
            <UpgradeCard />
          ) : !isAuthenticated ? (
            <SignUpCard />
          ) : null}

          {isAuthenticated && (
            <StudySetProgressCard studySetId={studySet.id} userId={userId} />
          )}

          <section className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-base">
                {t("questionsInThisSet")} (
                {questionsWithFileName?.questions?.length})
              </h4>
              <OrderQuestionDropdown isAuthenticated={isAuthenticated} />
            </div>
            <div
              id="section"
              ref={sectionRef}
              className="flex flex-col gap-6 w-full"
            >
              <QuestionsList
                questions={filteredQuestions}
                status={questionStatus}
                isFetching={isFetching}
                isPolling={questionsWithFileName?.status.shouldPoll}
                retry={() => refetch()}
                showActions={studySet.isOwner}
              />

              {showCta && (
                <div className="relative">
                  {/* Blurred Demo Cards */}
                  <div className="space-y-4 filter blur-[4px] pointer-events-none">
                    {/* First Demo Card */}
                    <div className="relative w-full border bg-muted/40 p-4 rounded-2xl md:h-[180px]">
                      <div className="flex flex-col md:flex-row w-full h-full gap-6 flex-1 self-stretch">
                        <div className="w-full md:w-1/4 flex flex-col justify-center h-full">
                          <h2 className="text-sm font-normal leading-5">
                            What are the key principles of effective time
                            management?
                          </h2>
                        </div>
                        <Separator
                          className="md:h-full h-[1px] md:w-[1px]"
                          orientation="vertical"
                        />
                        <div className="w-full md:w-3/4 flex flex-col justify-center h-full">
                          <h2 className="text-sm font-normal leading-5">
                            The key principles include setting clear priorities,
                            creating structured schedules, and maintaining focus
                            on important tasks.
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Second Demo Card */}
                    <div className="relative w-full border bg-muted/40 p-4 rounded-2xl md:h-[180px]">
                      <div className="flex flex-col md:flex-row w-full h-full gap-6 flex-1 self-stretch">
                        <div className="w-full md:w-1/4 flex flex-col justify-center h-full">
                          <h2 className="text-sm font-normal leading-5">
                            How many carbons in hexane.
                          </h2>
                        </div>
                        <Separator
                          className="md:h-full h-[1px] md:w-[1px]"
                          orientation="vertical"
                        />
                        <div className="w-full md:w-3/4 flex flex-col justify-center h-full">
                          <h2 className="text-sm font-normal leading-5">
                            Effective communication enhances team collaboration
                            by ensuring clear expectations, reducing
                            misunderstandings, and fostering open dialogue
                            between team members.
                          </h2>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%]">
                    <div className="rounded-lg border bg-background p-6 shadow-xl">
                      <div className="flex flex-col items-center text-center gap-6">
                        <div className="space-y-1.5">
                          <h3 className="font-semibold text-2xl">
                            {t("studySet.cta.title")}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {t("studySet.cta.description")}
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            onOpenPricing({
                              paywallType:
                                PAYWALL_TYPES.UNLOCK_QUESTIONS_STUDYSET_PAGE,
                            });
                            posthog.capture("paywall_shown", {
                              userId: userId,
                              studySetId: studySet.id,
                              studySetName: studySet.name,
                              paywallType:
                                PAYWALL_TYPES.UNLOCK_QUESTIONS_STUDYSET_PAGE,
                            });
                          }}
                        >
                          {t("studySet.cta.unlockButton")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
      {studySet.type !== "public" && (
        <DeleteModal
          title={t("studySet.card.delete.title")}
          message={
            studySet.type === "personal"
              ? t("studySet.card.delete.description")
              : t("studySet.card.delete.unsave")
          }
          handleCancel={() => setIsDeleteOpen(false)}
          handleConfirm={() => {
            if (studySet.type === "personal") {
              deleteMutation.mutate(
                { id: studySet.id },
                {
                  onSuccess: () => setIsDeleteOpen(false),
                },
              );
            } else {
              handleUnlike();
            }
          }}
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          pending={deleteMutation.isPending}
        />
      )}
      <CreatePracticeTestModal
        source={{ kind: "studyset", studySetId: studySet.id }}
        open={isPracticeTestOpen}
        onOpenChange={setIsPracticeTestOpen}
      />
    </>
  );
};

export default StudySetPublicView;
